import type { TrainDetails } from "~/features/freeCapacityPush/useFCCState";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { Trans, useTranslation } from "react-i18next";
import { Divider, Stack, styled } from "~/common/mui";
import { InfoCircle } from "~/common/components/InfoCircle/InfoCircle";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { Alert } from "~/common/mui/Alert/Alert";
import { FCCTileCard, FCCTileSubtitle, FCCTileTitle, FCCTileTooltipContent } from "~/features/freeCapacityPush/components/FCCTileStyles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface Props {
  className?: string;
  data?: TrainDetails;
  trainLabel?: string;
  trainSelected?: boolean;
}

/*

 */

const Root = styled(FCCTileCard)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(4),
  alignItems: "center",
  [theme.breakpoints.up("desktop-s")]: {
    display: "grid",
    gap: "unset",
    gridTemplate: `
      "titleSection infoIcon" auto
      "capaSection capaSection" auto
      "divider divider" auto
      "content content" auto / 1fr auto
    `,
  },
}));

const TitleSection = styled(Stack)(({ theme }) => ({
  alignSelf: "flex-start",
  gridArea: "titleSection",
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(2),
  },
}));

const InfoIcon = styled(InfoCircle)({
  gridArea: "infoIcon",
  position: "relative",
  top: "unset",
  right: "unset",
  alignSelf: "flex-start",
});

const NoTrain = styled("div")(({ theme }) => ({
  gridArea: "content",
  ...theme.typography.headlineSmall,
  fontWeight: "bold",
  flex: 1,
}));

const ContentSection = styled("div")({
  gridArea: "content",
  flex: "1 1 auto",
  display: "flex",
  flexDirection: "column",
});

const CapacitySection = styled("div")(({ theme }) => ({
  gridArea: "capaSection",
  flex: "1 1 auto",
  color: theme.palette.text.secondary,
  display: "grid",
  columnGap: theme.spacing(1),
  alignItems: "center",
  gridTemplate: `
    "amount pcs" auto
    "avg avg" auto / auto 1fr
  `,
  "> *:nth-of-type(1)": { gridArea: "amount" },
  "> *:nth-of-type(2)": { gridArea: "pcs" },
  "> *:nth-of-type(3)": { gridArea: "avg" },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  [theme.breakpoints.up("desktop-s")]: {
    gridArea: "divider",
    margin: theme.spacing(2, 0, 1),
  },
}));

const InfoAlert = styled(Alert)(({ theme }) => ({
  [theme.breakpoints.up("desktop-s")]: {
    gridArea: "content",
    marginTop: theme.spacing(4),
  },
}));

const RltSection = styled("div")(({ theme }) => ({
  display: "grid",
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(1),
  gridTemplateColumns: "minmax(54px, auto) minmax(0, 1fr)",
  color: theme.palette.text.secondary,
  ...theme.typography.bodyMedium,
  label: { color: "inherit" },
  em: {
    color: theme.palette.text.primary,
    fontWeight: "bold",
    fontStyle: "unset",
    ...theme.typography.bodyLarge,
  },
}));

const RltValue = styled(Stack)({
  alignItems: "center",
  "& i": {
    transform: "rotate(180deg)",
  },
});

const Pieces = styled("span")(({ theme }) => ({
  ...theme.typography.headlineMedium,
  fontWeight: "bold",
  color: theme.palette.text.primary,
}));

export const FreeCapacityTile = ({ data, className, trainLabel, trainSelected }: Props) => {
  const { t } = useTranslation();
  const trainId = trainLabel ?? t("page.fcc.freeCapacity.train");

  const hasTrain = !!data;

  const showDates = data && data.validFrom && data.validTo;
  const showAverageRlt = !!data?.avgRlt;
  const validQty = data?.avgPerMonthPcs && data.avgPerMonthPcs > 0;

  const amount = validQty ? NF.getDefaultFormatter().format(data.avgPerMonthPcs!) : "N/A";

  const popupContent = (
    <FCCTileTooltipContent>
      <Trans i18nKey={"page.fcc.freeCapacity.tooltip"} />
    </FCCTileTooltipContent>
  );

  const isDesktop = useIsMinDesktopSize();
  return (
    <Root className={className}>
      <TitleSection gap={isDesktop ? 0 : 1}>
        <FCCTileTitle>
          <span>{t("page.fcc.freeCapacity.title")}</span>
        </FCCTileTitle>
        <FCCTileSubtitle>{trainId}</FCCTileSubtitle>
      </TitleSection>

      {trainSelected && (
        <CapacitySection>
          <Pieces>{amount}</Pieces>
          <span>{t("page.fcc.freeCapacity.pcs")}</span>
          <span>{t("page.fcc.freeCapacity.avg")}</span>
        </CapacitySection>
      )}

      {(!isDesktop || (trainSelected && (showDates || showAverageRlt))) && (
        <StyledDivider
          orientation={isDesktop ? "horizontal" : "vertical"}
          flexItem={!isDesktop}
        />
      )}

      {!trainSelected && <NoTrain>{t("page.fcc.freeCapacity.noTrain")}</NoTrain>}

      {trainSelected && (showDates || showAverageRlt) && (
        <ContentSection>
          <RltSection>
            {showDates && (
              <>
                <label>{t("page.fcc.freeCapacity.valid")}</label>

                <RltValue direction={"row"} gap={0.5}>
                  <em>{data?.validFrom?.format("MMM")}</em>
                  <span>{data?.validFrom?.format("YYYY")}</span>
                  <ArrowBackIcon sx={{ fontSize: 12 }} />
                  <em>{data?.validTo?.format("MMM")}</em>
                  <span>{data?.validTo?.format("YYYY")}</span>
                </RltValue>
              </>
            )}
            {showAverageRlt && (
              <>
                <label>{t("page.fcc.freeCapacity.averageRlt")}</label>

                <RltValue direction={"row"} gap={0.5}>
                  <em>{NF.getDefaultFormatter().format(data!.avgRlt!)}</em>
                  <span>{t("page.fcc.freeCapacity.day", { count: data!.avgRlt! })}</span>
                </RltValue>
              </>
            )}
          </RltSection>
        </ContentSection>
      )}

      {hasTrain && !validQty && (
        <InfoAlert severity={"info"}>
          {t("page.fcc.freeCapacity.noCapacity")}
        </InfoAlert>
      )}

      <InfoIcon
        content={popupContent}
        placement={"left"}
      />
    </Root>
  );
};
